import { useId } from "react";
import type { WeightPoint, WeightUnit } from "../types";

interface WeightChartProps {
  points: WeightPoint[];
  unit: WeightUnit;
}

const WIDTH = 720;
const HEIGHT = 280;
const PADDING = { top: 24, right: 18, bottom: 42, left: 54 };

export function WeightChart({ points, unit }: WeightChartProps) {
  const titleId = useId();
  const descriptionId = useId();

  if (points.length === 0) {
    return (
      <div className="empty-state empty-state--chart">
        <strong>Your weight chart will appear here</strong>
        <p>Log a weight to start seeing your daily readings and five-day trend.</p>
      </div>
    );
  }

  const allValues = points.flatMap((point) => [point.weight, point.rollingAverage])
    .filter((value): value is number => value !== undefined);
  if (allValues.length === 0) {
    return (
      <div className="empty-state empty-state--chart">
        <strong>Your weight chart will appear here</strong>
        <p>Log a weight to start seeing your daily readings and five-day trend.</p>
      </div>
    );
  }
  const rawMin = Math.min(...allValues);
  const rawMax = Math.max(...allValues);
  const spread = Math.max(rawMax - rawMin, 0.5);
  const min = rawMin - spread * 0.18;
  const max = rawMax + spread * 0.18;
  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  const xFor = (index: number) =>
    PADDING.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
  const yFor = (value: number) => PADDING.top + ((max - value) / (max - min)) * plotHeight;

  const rawSegments: string[] = [];
  let rawSegmentOpen = false;
  points.forEach((point, index) => {
    if (point.weight === undefined) {
      rawSegmentOpen = false;
      return;
    }
    rawSegments.push(`${rawSegmentOpen ? "L" : "M"} ${xFor(index)} ${yFor(point.weight)}`);
    rawSegmentOpen = true;
  });
  const averageSegments: string[] = [];
  let segmentOpen = false;
  points.forEach((point, index) => {
    if (point.rollingAverage === undefined) {
      segmentOpen = false;
      return;
    }
    averageSegments.push(`${segmentOpen ? "L" : "M"} ${xFor(index)} ${yFor(point.rollingAverage)}`);
    segmentOpen = true;
  });

  const tickValues = [max, min + (max - min) / 2, min];
  const labelIndices = Array.from(new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]));
  const rawReadingCount = points.filter((point) => point.weight !== undefined).length;
  const trendPointCount = points.filter((point) => point.rollingAverage !== undefined).length;

  return (
    <>
      <div className="chart-legend" aria-hidden="true">
        <span><i className="chart-legend__dot chart-legend__dot--raw" />Daily weight</span>
        <span><i className="chart-legend__line" />Five-day average</span>
      </div>
      <div className="weight-chart">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-labelledby={`${titleId} ${descriptionId}`}>
          <title id={titleId}>Weight history with five-day rolling average</title>
          <desc id={descriptionId}>
            {rawReadingCount} daily weight readings and {trendPointCount} rolling-average points in {unit}. Gaps in the daily line are calendar days without a raw reading. The darker line is the average of available readings from each calendar day and its four preceding days.
          </desc>

          {tickValues.map((tick) => {
            const y = yFor(tick);
            return (
              <g key={tick}>
                <line className="weight-chart__grid" x1={PADDING.left} x2={WIDTH - PADDING.right} y1={y} y2={y} />
                <text className="weight-chart__axis-label" x={PADDING.left - 10} y={y + 4} textAnchor="end">
                  {tick.toFixed(1)}
                </text>
              </g>
            );
          })}

          <path className="weight-chart__raw-line" d={rawSegments.join(" ")} />
          <path className="weight-chart__average-line" d={averageSegments.join(" ")} />

          {points.map((point, index) => (
            <g key={point.date}>
              {point.weight !== undefined && (
                <circle className="weight-chart__point" cx={xFor(index)} cy={yFor(point.weight)} r="5">
                  <title>{`${point.label}: ${point.weight.toFixed(1)} ${unit}${point.rollingAverage === undefined ? "" : `, five-day average ${point.rollingAverage.toFixed(1)} ${unit}`}`}</title>
                </circle>
              )}
              {point.rollingAverage !== undefined && (
                <circle className="weight-chart__average-point" cx={xFor(index)} cy={yFor(point.rollingAverage)} r="3.5">
                  {point.weight === undefined && <title>{`${point.label}: no weight recorded, five-day average ${point.rollingAverage.toFixed(1)} ${unit}`}</title>}
                </circle>
              )}
            </g>
          ))}

          {labelIndices.map((index) => (
            <text
              key={points[index].date}
              className="weight-chart__date-label"
              x={xFor(index)}
              y={HEIGHT - 14}
              textAnchor={index === 0 ? "start" : index === points.length - 1 ? "end" : "middle"}
            >
              {points[index].label}
            </text>
          ))}
        </svg>
      </div>

      <details className="details-panel chart-data">
        <summary>View chart as a table</summary>
        <div className="table-scroll">
          <table>
            <thead><tr><th>Date</th><th>Weight</th><th>5-day average</th></tr></thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.date}>
                  <td>{point.label}</td>
                  <td>{point.weight === undefined ? "Not recorded" : `${point.weight.toFixed(1)} ${unit}`}</td>
                  <td>{point.rollingAverage === undefined ? "Not available" : `${point.rollingAverage.toFixed(1)} ${unit}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}
